import FormResponse from '../model/FormResponse';
import m from './module';
import { createError } from '../error';

class FormResponseService {
  constructor(
    $q,
    LogService,
    FieldResponseService,
    FormService,
    ProductService
  ) {
    this.$q = $q;
    this.LogService = LogService;
    this.FieldResponseService = FieldResponseService;
    this.FormService = FormService;
    this.ProductService = ProductService;
  }

  create(data) {
    data = data || {};
    const promises = [];

    const formResponseId = data.id || null;
    const formId = data.events_custom_forms_id || null;
    const recapDefinitionId = data.recapDefinitionId || data.events_recaps_id || null;
    const eventTimezoneName = data.eventTimezoneName || null;

    // Load form if not present
    let formPromise;
    if (data.form) {
      formPromise = this.$q.when(data.form);
    } else {
      if (!formId) {
        throw createError({
          error: 'MissingForm',
          message: `Form is required for FormResponse; neither form nor form ID are present for FormResponse ${formResponseId} on recap definition ${recapDefinitionId}`,
        });
      } else {
        formPromise = this.$q
          .when(this.FormService.getById(formId))
          .then(form => {
            if (form) {
              data.form = form;
            } else {
              this.LogService.error(`FormResponseService.create() unable to retrieve form ${formId} for FormResponse ${formResponseId} on recap definition ${recapDefinitionId}`);
              return this.$q.reject(createError({
                error: 'MissingForm',
                message: `FormResponseService.create() unable to retrieve form ${formId} for FormResponse ${formResponseId} on recap definition ${recapDefinitionId}`,
              }));
            }
          });
      }
    }

    // Create field responses
    let fieldResponseDataArr = data.field_responses || [];
    let loadFieldResponse = fieldResponseData => this.FieldResponseService
      .create({
        ...fieldResponseData,
        recapDefinitionId,
        eventTimezoneName,
      })
      .catch(err => {
        let fieldResponseId = fieldResponseData.id || null;
        this.LogService.warn(`FormResponseService.create() swallowing error creating FieldResponse ${fieldResponseId} for FormResponse ${formResponseId} Form ${formId} on recap definition ${recapDefinitionId}. Error:`, err, "data:", fieldResponseData);
        return null;
      });

    // Load form, and if successful, field responses. No use trying to load field responses if form isn't present!
    promises.push(
      formPromise
        .then(form => {
          let fieldResponsePromises = fieldResponseDataArr.map(fieldResponseData => loadFieldResponse(fieldResponseData));
          return this.$q.all(fieldResponsePromises);
        })
        .then(fieldResponses => {
          // Filter out empty field responses
          data.field_responses = fieldResponses.filter(fieldResponse => !!fieldResponse);
          return data.field_responses;
        })
    );

    // Load product if not present.
    // Merchandising-mode responses store product_labels_id; the resolved product
    // is the synthetic merchandising row already present on form.products (set
    // by the merchandising scope overlay). When neither in-memory nor lookup
    // produces a row we leave data.product null - getProductWithId() returns
    // null for unknown ids, so a stale product_labels_id (e.g. activity scope
    // changed since the response was last saved) doesn't block load.
    if (data.product_labels_id) {
      promises.push(
        formPromise
          .then(form => {
            const product = form && form.getProductWithId && form.getProductWithId(data.product_labels_id);
            if (product) {
              data.product = product;
            } else {
              this.LogService.warn(
                "form.getProductWithId() did not return merchandising product for product_labels_id",
                data.product_labels_id,
                "- response will load without product attribution"
              );
            }
          })
      );
    } else if (data.products_id) {
      promises.push(
        formPromise
          .then(form => {
            const product = form && form.getProductWithId && form.getProductWithId(data.products_id);
            if (product) {
              return product;
            } else {
              return this.ProductService.getById(data.products_id);
            }
          })
          .then(product => {
            if (!product) {
              this.LogService.error("form.getProductWithId() and ProductService.getById() did not return product for ID", data.products_id);
              return this.$q.reject(createError({
                error: 'UnableToRetrieveProductById',
                description: 'Unable to retrieve specified product',
              }));
            }
            data.product = product;
          })
      );
    }

    return this.$q.all(promises)
      .then(() => {
        const formResponse = new FormResponse(data);

        // Maintain back-references from field responses on this form response
        formResponse.field_responses.forEach(fieldResponse => {
          fieldResponse.formResponse = formResponse;
        });

        return formResponse;
      });
  }

  serialize(formResponse) {
    const outOfStock = !!(formResponse.out_of_stock && formResponse.canMarkOutOfStock);
    return {
      id: formResponse.id,
      events_custom_responses_id: formResponse.events_custom_responses_id,
      events_custom_forms_id: formResponse.events_custom_forms_id,
      products_id: formResponse.products_id,
      product_labels_id: formResponse.product_labels_id,
      merchandising_activities_id: formResponse.merchandising_activities_id,
      customers_id: formResponse.customers_id,
      sampling_feedback: formResponse.sampling_feedback,
      out_of_stock: outOfStock,
      field_responses: outOfStock ? [] : formResponse.collectVisibleFieldResponses().map(fieldResponse => this.FieldResponseService.serialize(fieldResponse)),
    };
  }
}

m.service('FormResponseService', FormResponseService);
